/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import {
    IBuyboxAddToCartViewProps,
    IBuyboxAddToWishlistViewProps,
    IBuyboxKeyInPriceViewProps,
    IBuyboxProductConfigureDropdownViewProps,
    IBuyboxProductConfigureViewProps,
    IBuyboxProductQuantityViewProps
} from '@msdyn365-commerce-modules/buybox';
import { IQuickviewViewProps } from '../../../modules/quickview/./quickview';
import { getAttributeValuesAsync } from "@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g";
import { findAttribute, findAttributeValue } from "../utilities/productListExtensionHelpers";

const _renderAddToCart = (addToCart: IBuyboxAddToCartViewProps): JSX.Element => {
    const { ContainerProps, errorBlock, button } = addToCart;

    return (
        <Node {...ContainerProps}>
            {errorBlock}
            {button}
        </Node>
    );
};

const _renderAddToWishlist = (addToWishlist: IBuyboxAddToWishlistViewProps): JSX.Element => {
    const { ContainerProps, errorBlock, button } = addToWishlist;

    return (
        <Node {...ContainerProps}>
            {errorBlock}
            {button}
        </Node>
    );
};

const _renderConfigureDropdown = (dropdown: IBuyboxProductConfigureDropdownViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, errors, select } = dropdown;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>
                {heading}
                {errors}
            </Node>
            {select}
        </Node>
    );
};

const _renderQuickViewPopup = (props: IQuickviewViewProps): JSX.Element => {
    const {
        ModalContainer,
        ModalHeaderContainer,
        ModalFooterContainer,
        ModalHeaderContent,
        ModalBodyContainer,
        addToWishlist,
        addToCart,
        cartContainerProps,
        productDetails
    } = props;

    let gtmJson: string | null = null;
    if (productDetails && productDetails.product) {
        const productInfo = productDetails.product;
        const gtmProduct = {
            name: productInfo.Name,
            id: productInfo.ItemId,
            price: productDetails.productPrice?.CustomerContextualPrice ?? productInfo.Price
        };

        gtmJson = JSON.stringify(gtmProduct);
    }

    return (
        <Module {...ModalContainer}>
            <Node {...ModalHeaderContainer}>{ModalHeaderContent}</Node>
            <Node {...ModalBodyContainer}>{_renderBodyContent(props)}</Node>
            <Node {...ModalFooterContainer}>
                <Node {...cartContainerProps}>
                    {addToCart && _renderAddToCart(addToCart)}
                    {addToWishlist && _renderAddToWishlist(addToWishlist)}
                    {gtmJson && (
                        <script className='hei-gtm-product' type='application/json'>
                            {gtmJson}
                        </script>
                    )}
                </Node>
            </Node>
        </Module>
    );
};

const _renderQuantity = (quantity: IBuyboxProductQuantityViewProps, quantityLimitsMessages: React.ReactNode): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, input, errors } = quantity;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>
                {heading}
                {errors}
            </Node>
            {input}
            {quantityLimitsMessages}
        </Node>
    );
};

const _renderKeyInPrice = (keyInPrice: IBuyboxKeyInPriceViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, input } = keyInPrice;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>{heading}</Node>
            {input}
        </Node>
    );
};

const _renderConfigure = (configure: IBuyboxProductConfigureViewProps): JSX.Element => {
    const { ContainerProps, dropdowns } = configure;

    return <Node {...ContainerProps}>{dropdowns.map(_renderConfigureDropdown)}</Node>;
};

const _renderBodyContent = (props: IQuickviewViewProps): JSX.Element => {
    const {
        title,
        price,
        rating,
        keyInPrice,
        quantity,
        configure,
        inventoryLabel,
        quantityLimitsMessages,
        seeDetailsbutton,
        loading,
        productDetails,
        ProductInfoContainerProps,
        MediaGalleryContainerProps,
        CarouselProps,
        unitOfMeasure,
        resources: {
            seePriceInCartText
        },
        context: {
            actionContext,
            actionContext: {
                requestContext: {
                    apiSettings: {
                        channelId,
                        catalogId
                    }
                }
            }
        }
    } = props;
    const [hidePrice, setHidePrice] = React.useState<boolean>(false);
    const [productId, setProductId] = React.useState<number>(0);
    const recordId = productDetails?.product?.RecordId;
    if (recordId && productId === 0) {
        setProductId(recordId);
    }

    const getProductAttributes = () => {

        void getAttributeValuesAsync({ callerContext: actionContext }, productId, channelId, catalogId).then(data => {
            const hidePriceAttribute = findAttribute(data, 'See Price In Cart');
            if (hidePriceAttribute) {
                const hidePriceValue = findAttributeValue(hidePriceAttribute);
                if (typeof hidePriceValue === 'boolean' && hidePriceValue) {
                    setHidePrice(hidePriceValue);
                } else {
                    setHidePrice(false);
                }
            } else {
                setHidePrice(false);
            }
        });
    };
    React.useEffect(() => {
        if (productId) {
            getProductAttributes();
        }
    }, [productId])

    if (loading) {
        return <>{loading}</>;
    }

    const hasSerialNumber = productDetails?.product?.Behavior?.HasSerialNumber || false;

    return (
        <>
            <Node {...MediaGalleryContainerProps}>
                <Node {...CarouselProps} />
            </Node>
            <Node {...ProductInfoContainerProps}>
                {title}
                {hidePrice ? <p className={'hide-price hide-price--detail'}>{seePriceInCartText}</p> : price}
                {unitOfMeasure}
                {rating}
                {configure && _renderConfigure(configure)}
                {keyInPrice && _renderKeyInPrice(keyInPrice)}
                {quantity && !hasSerialNumber && _renderQuantity(quantity, quantityLimitsMessages)}
                {seeDetailsbutton}
                {inventoryLabel}
            </Node>
        </>
    );
};

/**
 * Functional component which renders quick view button and popup based by the given props file.
 * @param {IQuickviewViewProps} props - Configuration for the quick view component.
 * @returns {React.FC<IQuickviewViewProps>} - Functional component of the quick view.
 */
export const QuickViewFunctionalComponent: React.FC<IQuickviewViewProps> = (props: IQuickviewViewProps) => {
    const { quickView, quickViewButton, isModalOpen, isMobileDevice } = props;
    return (
        <Module {...quickView}>
            {!isMobileDevice && quickViewButton}
            {isModalOpen && _renderQuickViewPopup(props)}
        </Module>
    );
};

export default QuickViewFunctionalComponent;
